"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { createClient } from "@/lib/supabase/client";
import { isValidEmail } from "@/lib/validations";
import { captchaOptions } from "@/lib/turnstile";
import { useTurnstile } from "./useTurnstile";
import { BusyLabel } from "@/components/ui/Spinner";

type State = "idle" | "pending" | "success";

export default function ResetRequestForm() {
  const t = useTranslations("auth");
  const [state, setState] = useState<State>("idle");
  const [email, setEmail] = useState("");
  const tErr = useTranslations("authErrors");
  const [captchaMsg, setCaptchaMsg] = useState<string | null>(null);
  const captcha = useTurnstile("reset_password");

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!email.trim() || !isValidEmail(email)) return;
    // Before anything is sent: the widget has not produced a token yet.
    const blocked = captcha.guard();
    if (blocked) {
      setCaptchaMsg(tErr(blocked));
      return;
    }
    setCaptchaMsg(null);
    setState("pending");

    const supabase = createClient();
    // Always show "success" UI regardless of whether the email exists —
    // this prevents email enumeration attacks.
    await supabase.auth.resetPasswordForEmail(email.trim(), {
      // Dedicated route (NOT /auth/callback?next=...) — Supabase's redirect
      // allowlist strips query params, so we use a path-only URL.
      // Use window.location.origin so the redirect always matches the current
      // origin (prod, netlify preview, localhost) — PKCE cookie + redirect
      // origin must agree, otherwise exchangeCodeForSession fails silently.
      redirectTo: `${window.location.origin}/auth/reset`,
      ...captchaOptions(captcha.token),
    });
    captcha.reset();

    setState("success");
  }

  if (state === "success") {
    return (
      <div className="mt-21 rounded-card bg-navy/[0.04] px-21 py-21 text-sm leading-relaxed text-navy">
        <p className="font-semibold">{t("checkEmailTitle")}</p>
        <p className="mt-5 text-gray-600">
          {t.rich("resetSentBody", {
            email,
            b: (chunks) => <strong className="text-navy">{chunks}</strong>,
          })}
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-21 space-y-13"
      noValidate
      aria-label={t("resetFormAria")}
    >
      {captchaMsg && (
        <p role="alert" className="rounded-control bg-red-50 px-13 py-8 text-sm text-red-700">
          {captchaMsg}
        </p>
      )}
      <div className="flex flex-col gap-3">
        <label
          htmlFor="email"
          className="text-xs font-semibold uppercase tracking-wider text-navy"
        >
          {t("emailLabel")} <span aria-hidden="true">*</span>
          <span className="sr-only">{t("required")}</span>
        </label>
        <input
          id="email"
          type="email"
          name="email"
          required
          aria-required="true"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          className="rounded-control border border-navy/20 bg-white px-13 py-8 text-sm text-navy placeholder:text-gray-500 focus-visible:border-gold focus-visible:outline-none"
        />
      </div>

      {captcha.widget}

      <button
        type="submit"
        disabled={state === "pending" || !email.trim()}
        aria-disabled={state === "pending" || !email.trim()}
        className="mt-8 w-full rounded-control bg-gold py-13 text-sm font-semibold uppercase tracking-wider text-navy transition-colors hover:bg-gold-hover disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
      >
        <BusyLabel busy={state === "pending"}>{state === "pending" ? t("sending") : t("sendResetLink")}</BusyLabel>
      </button>
    </form>
  );
}
